import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  Workflow,
  WorkflowPlaceholder,
  WorkflowScheduledNotification,
  WorkflowStatus,
  WorkflowStatusChange,
  WorkflowStatusChangeTemplate,
} from "../models/workflow";

@Injectable({
  providedIn: "root",
})
export class WorkflowService {
  private baseUrl = "api/workflows";
  private statusBaseUrl = "api/workflowstatus";
  private statusChangeBaseUrl = "api/workflowstatuschange";
  private statusChangeTemplateBaseUrl = "api/workflowstatuschangetemplates";
  private statusNotificationBaseUrl = "api/workflowschedulednotifications";

  constructor(private http: HttpClient) {}

  getAll() {
    return this.http.get<Workflow[]>(`${this.baseUrl}`);
  }

  get(workflowId: string, languageId: string) {
    return this.http.get<Workflow>(
      `${this.baseUrl}/${workflowId}/${languageId}`
    );
  }

  getStatuses(workflowId: string) {
    return this.http.get<WorkflowStatus[]>(
      `${this.baseUrl}/${workflowId}/statuses`
    );
  }

  getStatuseChanges(workflowId: string) {
    return this.http.get<WorkflowStatusChange[]>(
      `${this.baseUrl}/${workflowId}/statusechanges`
    );
  }

  getNotifications(workflowId: string) {
    return this.http.get<WorkflowScheduledNotification[]>(
      `${this.baseUrl}/${workflowId}/notifications`
    );
  }

  update(workflow: Workflow): Observable<Workflow> {
    return this.http.put<Workflow>(`${this.baseUrl}/${workflow?.id}`, workflow);
  }

  getStatus(statusId: string) {
    return this.http.get<Workflow>(`${this.statusBaseUrl}/${statusId}`);
  }

  saveStatus(workflowstatus: WorkflowStatus) {
    if (workflowstatus.id) {
      return this.http.put<WorkflowStatus>(
        `${this.statusBaseUrl}/${workflowstatus?.id}`,
        workflowstatus
      );
    } else {
      workflowstatus.id = undefined;
      return this.http.post<WorkflowStatus>(
        `${this.statusBaseUrl}`,
        workflowstatus
      );
    }
  }

  deleteStatus(statusId: string) {
    return this.http.delete<WorkflowStatus>(
      `${this.statusBaseUrl}/${statusId}`
    );
  }

  saveStatusChange(obj: WorkflowStatusChange) {
    if (obj.id) {
      return this.http.put<WorkflowStatusChange>(
        `${this.statusChangeBaseUrl}/${obj?.id}`,
        obj
      );
    } else {
      obj.id = undefined;
      return this.http.post<WorkflowStatusChange>(
        `${this.statusChangeBaseUrl}`,
        obj
      );
    }
  }

  deleteStatusChange(id: string) {
    return this.http.delete<WorkflowStatusChange>(
      `${this.statusChangeBaseUrl}/${id}`
    );
  }

  getStatusChangeTemplate(id: string) {
    return this.http.get<WorkflowStatusChangeTemplate>(
      `${this.statusChangeTemplateBaseUrl}/${id}`
    );
  }

  getStatusChangeTemplateProof(workflowChangeId: string, administrativeUnitId: string, templateId: string) {
    return this.http.get<any>(
      `${this.statusChangeTemplateBaseUrl}/getWorkflowStatusChangeTemplateProof/${workflowChangeId}/${administrativeUnitId}/${templateId}`
    );
  }

  getWorkflowPlaceholders(workflowId: string): Observable<WorkflowPlaceholder[]> {
    return this.http.get<WorkflowPlaceholder[]>(
      `${this.statusChangeTemplateBaseUrl}/placeholders/${workflowId}`
    );
  }

  saveStatusChangeTemplate(
    obj: WorkflowStatusChangeTemplate,
    workflowStatusChangeId: string
  ) {
    if (obj.id) {
      return this.http.put<WorkflowStatusChangeTemplate>(
        `${this.statusChangeTemplateBaseUrl}/${obj?.id}`,
        obj
      );
    } else {
      obj.id = undefined;
      return this.http.post<WorkflowStatusChangeTemplate>(
        `${this.statusChangeTemplateBaseUrl}/${workflowStatusChangeId}`,
        obj
      );
    }
  }

  deleteStatusChangeTemplate(id: string, workflowStatusChangeId: string) {
    return this.http.delete<WorkflowStatusChangeTemplate>(
      `${this.statusChangeTemplateBaseUrl}/${workflowStatusChangeId}/${id}`
    );
  }

  saveStatusNotification(obj: WorkflowScheduledNotification) {
    if (obj.id) {
      return this.http.put<WorkflowScheduledNotification>(
        `${this.statusNotificationBaseUrl}/${obj?.id}`,
        obj
      );
    } else {
      obj.id = undefined;
      return this.http.post<WorkflowScheduledNotification>(
        `${this.statusNotificationBaseUrl}`,
        obj
      );
    }
  }

  deleteStatusNotification(id: string) {
    return this.http.delete<WorkflowScheduledNotification>(
      `${this.statusNotificationBaseUrl}/${id}`
    );
  }

  cloneStatusNotification(id: string) {
    return this.http.get<WorkflowScheduledNotification>(
      `${this.statusNotificationBaseUrl}/${id}/clone`
    );
  }

  changeSurveyStatus(
    workFlowId: string,
    administrativeUnitSurveyId: string,
    toWorkflowStatusId: string,
    indicatorType: number,
    languageId: string
  ) {
    var request = {
      workFlowId: workFlowId,
      administrativeUnitSurveyId: administrativeUnitSurveyId,
      toWorkflowStatusId: toWorkflowStatusId,
      indicatorType: indicatorType,
      languageId: languageId,
    };
    return this.http.put<void>(
      `${this.statusBaseUrl}/update-administrativeUnit-survey-status`,
      request
    );
  }
}
